'use client';

import React, { useState } from 'react';
import AppLayout from '@/components/AppLayout';
import DashboardHeader from './components/DashboardHeader';
import KPIBentoGrid from './components/KPIBentoGrid';
import BrandedTrendChart from './components/BrandedTrendChart';
import TopQueriesChart from './components/TopQueriesChart';
import SpikeEventsTable from './components/SpikeEventsTable';
import AlertConfigPanel from './components/AlertConfigPanel';
import GSCConnectBanner from './components/GSCConnectBanner';
import AboveAverageDays from './components/AboveAverageDays';
import { Toaster } from 'sonner';
import { Search } from 'lucide-react';

export default function BrandedTrafficDashboard() {
  const [connectedSiteUrl, setConnectedSiteUrl] = useState<string>('');
  const [brandedKeyword, setBrandedKeyword] = useState<string>('');
  const [keywordInput, setKeywordInput] = useState<string>('');

  const handleKeywordSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setBrandedKeyword(keywordInput.trim());
  };

  return (
    <AppLayout>
      <Toaster position="bottom-right" richColors />
      {/* Smarty.marketing rose-tinted background */}
      <div className="min-h-screen" style={{ backgroundColor: 'rgba(150, 25, 50, 0.03)' }}>
        <div className="max-w-screen-2xl mx-auto px-6 lg:px-8 xl:px-10 py-6 space-y-6">
          <DashboardHeader />

          {/* GSC OAuth Connect Banner */}
          <GSCConnectBanner
            onConnected={setConnectedSiteUrl}
            selectedSiteUrl={connectedSiteUrl}
          />

          {/* Branded Keyword Filter — shown only when GSC is connected */}
          {connectedSiteUrl && (
            <form onSubmit={handleKeywordSubmit} className="flex items-center gap-3">
              <div className="relative flex-1 max-w-sm">
                <Search
                  size={14}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-[#696969]"
                />
                <input
                  type="text"
                  value={keywordInput}
                  onChange={(e) => setKeywordInput(e.target.value)}
                  placeholder="Branded keyword filter (e.g. acme)"
                  className="w-full pl-9 pr-3 py-2 text-sm border border-[#e7e7e7] rounded-[5px] bg-white text-[#39364E] focus:outline-none focus:ring-2 focus:ring-[rgba(150,25,50,0.3)] focus:border-[#961932] transition-all duration-150"
                />
              </div>
              <button
                type="submit"
                className="h-9 px-4 bg-[#961932] text-white rounded-[5px] text-sm font-medium hover:bg-[#8C0B31] transition-colors duration-150 btn-smarty-shadow"
              >
                Apply Filter
              </button>
              {brandedKeyword && (
                <button
                  type="button"
                  onClick={() => { setBrandedKeyword(''); setKeywordInput(''); }}
                  className="h-9 px-3 border border-[#e7e7e7] rounded-[5px] text-sm text-[#696969] hover:bg-[rgba(150,25,50,0.04)] hover:border-[rgba(150,25,50,0.3)] transition-colors duration-150"
                >
                  Clear
                </button>
              )}
              {brandedKeyword && (
                <span className="text-xs text-[#696969]">
                  Filtering for:{' '}
                  <span className="font-medium text-[#39364E]">&ldquo;{brandedKeyword}&rdquo;</span>
                </span>
              )}
            </form>
          )}

          <KPIBentoGrid />

          {/* Primary Chart Row */}
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-5">
            <div className="xl:col-span-2">
              <BrandedTrendChart />
            </div>
            <div className="xl:col-span-1">
              <TopQueriesChart />
            </div>
          </div>

          {/* Bottom Row: Table + Alert Config */}
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-5">
            <div className="xl:col-span-2">
              <SpikeEventsTable />
            </div>
            <div className="xl:col-span-1 space-y-5">
              {connectedSiteUrl && (
                <AboveAverageDays
                  siteUrl={connectedSiteUrl}
                  brandedKeyword={brandedKeyword}
                  days={90}
                />
              )}
              <AlertConfigPanel />
            </div>
          </div>
        </div>
      </div>
    </AppLayout>
  );
}